import React, { useMemo, useState } from "react";
import { Copy, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// F2B · Shared temporary-password field with:
//   * Show / Hide toggle
//   * Copy-to-clipboard
//   * Generate (client-side crypto-strong; server also generates when
//     the field is submitted blank — this button gives the admin an
//     immediate value to review before submit)
//
// The parent owns state (`value`, `onChange`) so it can validate + submit.
// `readOnly` renders it as a display-only field (post-create modal).
export function TemporaryPasswordField({
  value,
  onChange,
  label = "Temporary Password",
  testidPrefix = "temp-pw",
  readOnly = false,
  autoFocus = false,
  minLen = 8,
}) {
  const [show, setShow] = useState(false);

  const generate = () => {
    // crypto.getRandomValues → 12 random bytes → base64url ≈ 16 chars ≈ 96 bits.
    const bytes = new Uint8Array(12);
    window.crypto.getRandomValues(bytes);
    const b64 = btoa(String.fromCharCode(...bytes))
      .replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
    onChange(b64);
    setShow(true);
  };

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Password copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-steel">{label}</label>
      <div className="mt-0.5 flex gap-1">
        <div className="relative flex-1">
          <input
            data-testid={`${testidPrefix}-input`}
            type={show ? "text" : "password"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
            autoFocus={autoFocus}
            placeholder={readOnly ? "" : "Leave blank to auto-generate on save"}
            className="w-full border border-coach-silver rounded-sm text-[13px] px-2 py-1.5 font-mono pr-16"
          />
          <button type="button" onClick={() => setShow((s) => !s)}
                  data-testid={`${testidPrefix}-toggle`}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-[11px] text-coach-blue px-1 flex items-center gap-0.5">
            {show ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
          </button>
        </div>
        {!readOnly && (
          <button type="button" onClick={generate}
                  data-testid={`${testidPrefix}-generate`}
                  className="h-9 px-2.5 rounded-sm text-[11.5px] font-medium border border-coach-silver hover:bg-road-white flex items-center gap-1">
            <RefreshCw size={12} /> Generate
          </button>
        )}
        <button type="button" onClick={copy} disabled={!value}
                data-testid={`${testidPrefix}-copy`}
                className="h-9 px-2.5 rounded-sm text-[11.5px] font-medium border border-coach-silver hover:bg-road-white disabled:opacity-40 flex items-center gap-1">
          <Copy size={12} /> Copy
        </button>
      </div>
      {!readOnly && (
        <div className="text-[10.5px] text-steel mt-1">
          At least {minLen} characters. Leave blank to have the server generate a
          strong value on save.
        </div>
      )}
    </div>
  );
}

// F2B · One-time credentials confirmation shown after Create User / Reset
// Password. Includes prominent Copy and warning that this is the only
// display.
export function CredentialResultDialog({ open, onClose, email, password, actionLabel = "created" }) {
  const [showPw, setShowPw] = useState(false);
  if (!open) return null;

  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(`${email}\n${password}`);
      toast.success("Email + password copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
         data-testid="credential-result">
      <div className="bg-white border border-coach-silver rounded-md shadow-xl max-w-lg w-full">
        <div className="bg-carbon text-white px-5 py-3 text-[12px] uppercase tracking-widest">
          User {actionLabel} — Copy Credentials Now
        </div>
        <div className="p-5 space-y-4 text-[13px]">
          <div className="text-carbon">
            The temporary password below will not be shown again. Copy it now
            and communicate to the user through your preferred channel.
          </div>
          <div className="space-y-2">
            <Row label="Login Email" value={email} testid="cred-email" />
            <Row label="Temporary Password" value={password}
                 testid="cred-password" mono
                 masked={!showPw}
                 rightAdornment={
                   <button onClick={() => setShowPw((s) => !s)}
                           data-testid="cred-toggle"
                           className="text-[11px] text-coach-blue">
                     {showPw ? "Hide" : "Show"}
                   </button>
                 } />
          </div>
          <div className="text-[11px] text-steel">
            The user must complete a required password change at their first
            login before entering All Access.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={copyBoth}
                    data-testid="cred-copy-both"
                    className="h-9 px-3 rounded-sm text-[12px] border border-coach-silver hover:bg-road-white">
              Copy both
            </button>
            <button onClick={onClose}
                    data-testid="cred-close"
                    className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, testid, mono, masked, rightAdornment }) => {
  const displayValue = masked ? "•".repeat(Math.max(value?.length || 0, 8)) : value;
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-widest text-steel">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        <div
          data-testid={testid}
          className={
            "flex-1 border border-coach-silver rounded-sm bg-road-white px-2.5 py-1.5 " +
            (mono ? "font-mono text-[13px]" : "text-[13px]")
          }
        >
          {displayValue}
        </div>
        {rightAdornment}
        <button onClick={async () => { await navigator.clipboard.writeText(value); toast.success("Copied"); }}
                data-testid={`${testid}-copy`}
                className="text-[11px] text-coach-blue px-1 whitespace-nowrap">
          Copy
        </button>
      </div>
    </div>
  );
};
